import { useState } from "react";
import { Col, Container, Row, Table, FormCheck, Button } from "react-bootstrap";
import { AllocateForm, GenSubnetForm, PoolForm } from "../components/forms/allocate_form";
import { useHeaderTags } from "../components/custom_hook";
import { SummaryFolder } from "../components/summary_folder";
import { HowMany4Nets, HowMany6Nets } from "../components/forms/subnet_form";
import { PoolFlow } from "../pages/pool_flow";

export const AllocateFlow = (props) => {
  const [prefixes, setPrefixes] = useState();
  const [showForm, setShowForm] = useState(true);
  const [selectForm, setSelectForm] = useState('none');
  const [desc, setShowDesc] = useState("true");
  const [refresh, setRefresh] = useState(true);

  useHeaderTags(props);

  const percentFrom = (total, remain) => {
    // returns a percent as a string, 2 decimal places
    let num = 100 * (1 - (remain / (total + remain))).toFixed(4)
    return num.toString().slice(0, 4);
  }

  const followMe = (e) => {
    let val = e.target.textContent;
    document.location.href = "/act/app/subnet/" + val.replace("/", "_");
  }

  const chooseForm = (e) => {
    let name = e.currentTarget.dataset.form;
    setSelectForm(name);
    switch (name) {
      case "hosts":
        e.currentTarget.querySelector("#hosts").checked = true;
        document.querySelector("#nets").checked = false;
        document.querySelector("#pool").checked = false;
        setPrefixes(null);
        break;
      case "nets":
        e.currentTarget.querySelector("#nets").checked = true;
        document.querySelector("#hosts").checked = false;
        document.querySelector("#pool").checked = false;
        setPrefixes(null);
        break;
      case "pool":
        e.currentTarget.querySelector("#pool").checked = true;
        document.querySelector("#hosts").checked = false;
        document.querySelector("#nets").checked = false;
        setPrefixes(null);
        break;
    }
  }

  let wasted = 0;
  if (prefixes && prefixes.pieSlices) 
    wasted = prefixes.lostHost * prefixes.pieSlices.length;

  return (  
    <Container variant="fluid">
      <h5>Generator type</h5>
      <div className="subform">
        <div data-form="pool" onClick={chooseForm} className={selectForm === 'pool' ? "text-bold" : "clickable"}><FormCheck type="radio" id="pool" label="Create an address pool" /></div>
        <div data-form="hosts" onClick={chooseForm} className={selectForm === 'hosts' ? "text-bold" : "clickable"}><FormCheck type="radio" active="true" id="hosts" label="Subnet space to cover a fixed number of hosts" /></div>
        <div data-form="nets" onClick={chooseForm} className={selectForm === 'nets' ? "text-bold" : "clickable"}><FormCheck type="radio" id="nets" label="Fixed number of subnets" /></div>
      </div>
    
      {showForm && (
        <><Row>{selectForm === 'hosts' && <Col><AllocateForm
          setPrefixes={setPrefixes}
          showForm={setShowForm}
          showDesc={setShowDesc}
        ></AllocateForm>
        </Col>}
          {selectForm === 'nets' && <Col><GenSubnetForm
            setPrefixes={setPrefixes}
            showForm={setShowForm}
            selectForm = {selectForm}
            showDesc={setShowDesc}
          ></GenSubnetForm>
          </Col>}
          {selectForm === 'pool' && <Col><PoolForm
            setPrefixes={setPrefixes}
            showForm={setShowForm}
            showDesc={setShowDesc}
            doRefresh={setRefresh}
            refresh={refresh}
          ></PoolForm>
          </Col>}
        </Row>
        {!prefixes?<Row><Col><HowMany4Nets/></Col><Col><HowMany6Nets/></Col></Row>:null}
        </>
      )}
      {prefixes && !desc && (
        selectForm === "pool"?
        <PoolFlow nets={prefixes} setRefresh={setRefresh} refresh={refresh}/>
        :<Container>
          {prefixes.err && <Container><div className="err">{prefixes.errMsg}</div></Container>}
          {prefixes.message && <Container><div className="msg">{prefixes.message}.</div></Container>}
          <Row>
            <Col id="prefixes">
              <div>
                <div className="text-bold">
                  Prefixes: {prefixes.pieSlices.length} <span className="fw-normal"> ({prefixes.aggMask})</span>
                </div>
                <pre className="half-panel">
                  <code>
                    {prefixes &&
                      prefixes.pieSlices.map((prefix, id) => {
                        return prefix.template ? <div>{prefix.template}</div> : <div key={id}>{prefix.label}</div>;
                      })}
                  </code>
                </pre>
              </div>
            </Col>
            {prefixes.remainder >= 0 ? <Col id="prefixes">
              <div>
                <div className="text-bold">
                  Allocated: {prefixes.size + prefixes.remainder}, Remainder IPs: {prefixes.remainder},  Efficiency: {percentFrom(prefixes.size, wasted + prefixes.remainder)}
                </div>
                <pre className="half-panel">
                  <code>
                    {prefixes && <div>from summary {prefixes.parent.label} ({prefixes.parent.size + 1})</div>}
                    {prefixes &&
                      prefixes.pieSlices.map((prefix, id) => {
                        return <div key={id}>&nbsp;- {prefix.label} ({prefix.size + 1 - prefixes.lostHost})</div>;
                      })}
                  </code>
                </pre>
              </div>
            </Col> : <Col> <pre className="half-panel">
                  <code>
              {prefixes.nodes && prefixes.nodes.map((subnet, id) => {
                return (
                  <SummaryFolder key={id} folder={subnet} setFresh={setRefresh} />
                );
              })}
            </code></pre></Col>}
          </Row>
        </Container>)}

      {(!prefixes || desc ) && (
      <Container>
      <div className="docs paper">
        <h3>Description</h3>
        <p>Our subnet allocation tool provides flexible methods to generate subnets from IPv4 and IPv6 address blocks. Whether you need to carve up an address pool, calculate subnets for a fixed number of hosts, or create uniform-sized subnets, this subnet calculator streamlines network address planning.</p>
        <p>The tool supports three different subnet generation methods:</p>
        <ul>
          <li><strong>Address Pool Allocator:</strong> Carve child subnets from a parent pool with specific sizes, leaving visibility into available space.</li>
          <li><strong>Host Coverage Calculator:</strong> Automatically calculate the optimal subnet sizes to cover a fixed number of hosts.</li>
          <li><strong>Fixed Subnet Generator:</strong> Create a fixed number of uniform-sized subnets from an anchor block.</li>
        </ul>
        
        <h3>Overview</h3>
        <p>Network address planning requires careful consideration of space efficiency and summarization. Our subnet allocation tool helps you optimize your address space allocation by selecting the right generation method for your use case.</p>
        <p>
          <strong>Address Pools:</strong> Pool allocation maps to summary routes. Use this method when you have a parent block (e.g., /21) and need to allocate specific child subnets (e.g., /26, /24) while tracking remaining available space.
        </p>
        <p>
          <strong>Host Coverage:</strong> Best for scenarios where you know the total number of hosts to accommodate but want the tool to calculate optimal subnet sizes. This method accounts for network and broadcast addresses to prevent over-allocation.
        </p>
        <p>
          <strong>Fixed Subnets:</strong> Use when you need a specific number of equally-sized subnets from an anchor block. Subnets will extend beyond the anchor block if needed, and subsequent allocations continue into adjacent blocks.
        </p>

        <h3>How to Use the Subnet Allocation Tool</h3>
        
        <h4>1. Address Pool Allocator</h4>
        <p><strong>Purpose:</strong> Allocate specific child subnets from a parent pool and identify remaining available space.</p>
        <p><strong>Inputs Required:</strong></p>
        <ul>
          <li>Parent pool address (e.g., 192.168.0.0/21)</li>
          <li>Desired child subnet sizes (e.g., /26, /24). You can also use multiplier notation such as <code>3x24</code> to request three /24 subnets.</li>
        </ul>
        <p>Note: the slash is optional for mask values in this tool, so <code>21 23</code> is treated the same as <code>/21 /23</code>.</p>
        <p><strong>Interpreting Output:</strong> The tool displays allocated subnets and the remaining pool space. This helps you understand what additional subnets can be allocated without conflicts.</p>
        
        <h4>2. Host Coverage Calculator</h4>
        <p><strong>Purpose:</strong> Automatically calculate subnet sizes needed to cover a specific number of hosts.</p>
        <p><strong>Inputs Required:</strong></p>
        <ul>
          <li>Address block (must be large enough for all hosts)</li>
          <li>Total number of hosts to cover</li>
          <li>Min and max subnet mask sizes (use same value for uniform sizing)</li>
          <li>Optional: Check "Account for unusable IPs" to reserve network and broadcast addresses</li>
        </ul>
        <p><strong>How It Works:</strong> The tool allocates subnets from the top of the block downward. When the max subnet mask covers all remaining hosts, smaller subnets are allocated in ascending order to prevent fragmentation.</p>
        <p><strong>Interpreting Output:</strong> View the calculated subnets, efficiency percentage, and remainder IPs. The efficiency metric shows how well the space is utilized.</p>
        
        <h4>3. Fixed Subnet Generator</h4>
        <p><strong>Purpose:</strong> Generate a fixed number of uniform-sized subnets from an anchor block.</p>
        <p><strong>Inputs Required:</strong></p>
        <ul>
          <li>Anchor block address (starting point)</li>
          <li>Number of subnets needed</li>
          <li>Either specific mask size or number of hosts per subnet</li>
        </ul>
        <p><strong>How It Works:</strong> Subnets are allocated from the top of the anchor block in descending order. If more subnets are needed than the anchor block can hold, allocation continues into adjacent blocks.</p>
        <p><strong>Interpreting Output:</strong> The output shows all allocated subnets with their sizes. If allocation spanned multiple blocks, each contiguous block is identified.</p>

        <h3>Common Features</h3>
        <ul>
          <li><strong>Mask Notation:</strong> Toggle between CIDR (192.168.1.0/24) and dotted decimal mask (192.168.1.0 255.255.255.0) notation.</li>
          <li><strong>Templates:</strong> Embed results into configuration snippets using variable substitutions.</li>
          <li><strong>IPv4 & IPv6:</strong> Full support for both address families. For IPv6, use /64 for host subnets.</li>
        </ul>

        <h3>Using Templates for Configuration</h3>
        <p>Templates allow you to embed allocation results into configuration files. Supported substitutions:</p>
        <div className="docs">
          <Table variant="light">
            <tbody>
              <tr><td><span className="code font-monospace">&#36;&#123;subnet&#125;</span></td><td>Subnet value, for example 192.168.1.0 255.255.255.0</td></tr>
              <tr><td><span className="code font-monospace">&#36;&#123;child&#125;</span></td><td>When subdividing, for example 192.168.1.64 255.255.255.192</td></tr>
              <tr><td><span className="code font-monospace">&#36;&#123;host&#125;</span></td><td>First IP address on the subnet, with mask.  For example 192.168.1.1 255.255.255.0</td></tr>
              <tr><td><span className="code font-monospace">&#36;&#123;index&#125;</span></td><td>Index number.  Useful for creating interfaces. For example, interface loopback&#36;&#123;index&#125;</td></tr>
              <tr><td><span className="code font-monospace">&#36;&#123;--&#125;</span></td><td>Section break.  Useful for looping back through the subnets in a subsequent section.</td></tr>
            </tbody></Table>
        </div>
        <p>The following example shows a template used to create interfaces and route them in BGP. If you use this example, select 'Mask' to get the proper notation.</p>
        <pre className="tab templateEx">
          <code className="code">
          interface GigabitEthernet 0/&#36;&#123;index&#125;<br/>
          &nbsp;&nbsp;&#36;&#123;host&#125;
          <br/>
          &#36;&#123;--&#125;
          <br/>!<br/>
          router bgp 65535<br/>
          &#36;&#123;--&#125;<br/>
          &nbsp;&nbsp;network &#36;&#123;subnet&#125;<br/>
          &nbsp;&nbsp;neighbor &#36;&#123;host&#125; remote-as 6500&#36;&#123;index&#125;<br/>
          </code></pre>

      </div>
      </Container>)}
    </Container>
  );
};
